import { useTranslate } from '@festina-life/ui/hooks'
import { useState } from 'react'
import { useParams } from 'react-router'

import {
  TransferButton,
  type TransferConfirmation,
} from '../../components/transferButton/TransferButton'
import { getDefaultTransferScope } from '../../components/transferScopeSelector/getDefaultTransferScope'
import {
  type TransferScope,
  TransferScopeSelector,
} from '../../components/transferScopeSelector/TransferScopeSelector'
import { useParameterContext } from '../../context/parameterContext'
import type { ParameterRouteParams } from '../../system/types'
import { useNavigationParams } from '../../system/useNavigationParams'

/**
 * Transfers the parameter in scope to an ancestor block, either its definition, its
 * values, or both.
 */
export const ParameterTransferButton = () => {
  const translate = useTranslate()
  const { block: currentViewBlock } = useNavigationParams()
  const { parameterDefinitionId } = useParams<ParameterRouteParams>()
  const { parameterDefinitionOperation, allParameterValueOperations } =
    useParameterContext()

  const [selectedScope, setSelectedScope] = useState<TransferScope>('both')

  const transferableDefinitionVersions =
    parameterDefinitionOperation?.block === currentViewBlock &&
    !parameterDefinitionOperation.annulOperations.length &&
    !parameterDefinitionOperation.isDraft
      ? [parameterDefinitionOperation]
      : []

  /**
   * The associated values of a parameter are its value operations of both kinds: an
   * amendment belongs to the value it amends, so transferring the values without it would
   * leave the destination block with the un-amended values.
   */
  const transferableValueVersions = allParameterValueOperations.filter(
    ({ block, annulOperations, isDraft }) =>
      block === currentViewBlock && !annulOperations.length && !isDraft,
  )

  const isDefinitionAvailable = transferableDefinitionVersions.length > 0
  const isValuesAvailable = transferableValueVersions.length > 0

  const scopeVersionCount = (() => {
    if (selectedScope === 'primary') {
      return transferableDefinitionVersions.length
    }
    if (selectedScope === 'associated') {
      return transferableValueVersions.length
    }
    return (
      transferableDefinitionVersions.length + transferableValueVersions.length
    )
  })()

  const handleConfirm: TransferConfirmation = async ({ transfer }) => {
    let addedToWorkspaceKey = undefined

    const shouldTransferDefinition =
      selectedScope === 'primary' ||
      (selectedScope === 'both' && isDefinitionAvailable)
    const shouldTransferValues =
      selectedScope === 'associated' ||
      (selectedScope === 'both' && isValuesAvailable)

    if (shouldTransferDefinition) {
      addedToWorkspaceKey = await transfer({
        operationName: parameterDefinitionId ?? '',
        operations: transferableDefinitionVersions,
        shouldInvalidateCacheAfterTransfer: !shouldTransferValues,
      })
    }
    if (shouldTransferValues) {
      addedToWorkspaceKey = await transfer({
        operationName: parameterDefinitionId ?? '',
        operations: transferableValueVersions,
        shouldInvalidateCacheAfterTransfer: true,
      })
    }

    return addedToWorkspaceKey
  }

  return (
    <TransferButton
      item={{
        itemId: parameterDefinitionId ?? '',
        type: 'parameterDefinition',
        operationName: parameterDefinitionId ?? '',
        versionCount: scopeVersionCount,
        isTransferable: isDefinitionAvailable || isValuesAvailable,
      }}
      transfer={{
        label: translate('parameterViewer.topBar.actions.transfer'),
        description: translate(
          'transfer.confirmationModal.parameter.description',
          { sourceBlock: currentViewBlock },
        ),
        onOpen: () =>
          setSelectedScope(
            getDefaultTransferScope({
              isPrimaryAvailable: isDefinitionAvailable,
              isAssociatedAvailable: isValuesAvailable,
            }),
          ),
        onConfirm: handleConfirm,
      }}
    >
      <TransferScopeSelector
        value={selectedScope}
        onChange={setSelectedScope}
        primaryLabel={translate('transfer.scope.primary.definition')}
        associatedLabel={translate('transfer.scope.associated.parameterValues')}
        isPrimaryAvailable={isDefinitionAvailable}
        isAssociatedAvailable={isValuesAvailable}
      />
    </TransferButton>
  )
}
